import { isDm, isThread, type ChannelDto } from '@lagerfeuer/shared';
import { AudioLines, Bell, BellOff, CalendarClock, Download, Inbox, Menu as MenuIcon, MessagesSquare, Pin, Puzzle, Search, Settings, UserPlus, Users, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Modal } from '../../components/Modal';
import { anchorFrom, type Anchor } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { useAppStore } from '../../store/app';
import { overlays, useOverlays } from '../../store/overlays';
import { dmName, isMuted } from '../../store/selectors';
import { useUi } from '../../store/ui';
import { extraMenuItems } from '../extras';
import { MarkdownInline } from '../messages/Markdown';
import { PinsPopover } from '../messages/PinsPopover';
import { ThreadsPopover } from '../threads/ThreadsPopover';
import { GroupDmSettings } from '../dms/GroupDmSettings';
import { Avatar } from '../../components/Avatar';
import { ChannelIcon } from './channelIcons';

/** Symbole bekannter Erweiterungen in der Kopfzeile (Rest: Puzzle). */
const EXTRA_ICONS: Record<string, LucideIcon> = { search: Search, 'schedule-message': CalendarClock, 'export-channel': Download, soundboard: AudioLines };

/** Kopfzeile eines Kanals: Name, Thema, Threads, Pins, Benachrichtigungen, Mitglieder, Erweiterungen. */
export function ChannelHeader({ channel, serverId }: { channel: ChannelDto; serverId: string | null }) {
  const navigate = useNavigate();
  const meId = useAppStore((s) => s.me?.id);
  const muted = useAppStore((s) => isMuted(s, 'CHANNEL', channel.id) || (!!serverId && isMuted(s, 'SERVER', serverId)));
  const unreadNotifications = useAppStore((s) => s.unreadNotifications);
  const pinUnread = useAppStore((s) => {
    const last = channel.lastPinAt;
    const seen = s.readStates[channel.id]?.lastPinAt;
    return !!last && (!seen || new Date(last) > new Date(seen));
  });
  const showMembers = useUi((s) => s.showMembers);
  const [pop, setPop] = useState<{ kind: 'pins' | 'threads'; anchor: Anchor } | null>(null);
  const [topicOpen, setTopicOpen] = useState(false);
  const [manageGroup, setManageGroup] = useState(false);
  const dm = isDm(channel.type);
  const other = dm && channel.type === 'DM' ? (channel.recipients ?? []).find((u) => u.id !== meId) : undefined;
  const name = dm ? dmName(channel, meId) : channel.name;
  const ctx = { serverId: serverId ?? undefined, channelId: channel.id };
  const extras = extraMenuItems.filter((i) => i.slot === 'channelHeader' && (!i.channelTypes || i.channelTypes.includes(channel.type)));
  const toggleMembers = () => {
    if (window.matchMedia?.('(max-width: 1100px)').matches) useUi.getState().setMembersOpen(!useUi.getState().membersOpen);
    else useUi.getState().toggleMembers();
  };

  return (
    <header className="channel-header" data-testid="channel-header">
      <button className="icon-btn mobile-only" onClick={() => useUi.getState().setNavOpen(true)} aria-label="Navigation öffnen" data-testid="open-nav">
        <MenuIcon size={20} />
      </button>
      {other ? <Avatar user={other} size={24} showStatus /> : <ChannelIcon type={channel.type} size={22} />}
      <h1 className="channel-title truncate" data-testid="channel-title">
        {name}
      </h1>
      {channel.topic && (
        <>
          <span className="header-sep" aria-hidden="true" />
          <button className="channel-topic truncate" onClick={() => setTopicOpen(true)} data-testid="channel-topic">
            <MarkdownInline content={channel.topic} serverId={serverId} />
          </button>
        </>
      )}
      <span className="spacer" />
      <div className="header-actions" role="toolbar" aria-label="Kanal-Aktionen">
        {!dm && !isThread(channel.type) && channel.type !== 'VOICE' && channel.type !== 'FORUM' && (
          <Tooltip label="Threads">
            <button className="icon-btn" onClick={(e) => setPop({ kind: 'threads', anchor: anchorFrom(e.currentTarget) })} data-testid="header-threads">
              <MessagesSquare size={20} />
            </button>
          </Tooltip>
        )}
        {serverId && (
          <Tooltip label={muted ? 'Benachrichtigungen (stumm)' : 'Benachrichtigungen'}>
            <button className="icon-btn" onClick={() => overlays.open({ type: 'notificationSettings', serverId, channelId: channel.id })} data-testid="header-notifications" aria-pressed={muted}>
              {muted ? <BellOff size={20} /> : <Bell size={20} />}
            </button>
          </Tooltip>
        )}
        {channel.type !== 'FORUM' && channel.type !== 'VOICE' && (
          <Tooltip label="Angeheftete Nachrichten">
            <button className="icon-btn has-dot" onClick={(e) => setPop({ kind: 'pins', anchor: anchorFrom(e.currentTarget) })} data-testid="header-pins" data-dot={pinUnread ? 'true' : undefined}>
              <Pin size={20} />
            </button>
          </Tooltip>
        )}
        {channel.type === 'GROUP_DM' && (
          <Tooltip label="Freunde zur Gruppe hinzufügen">
            <button className="icon-btn" onClick={() => overlays.open({ type: 'newDm', channelId: channel.id })} data-testid="header-add-recipient">
              <UserPlus size={20} />
            </button>
          </Tooltip>
        )}
        {channel.type === 'GROUP_DM' && (
          <Tooltip label="Gruppe verwalten">
            <button className="icon-btn" onClick={() => setManageGroup(true)} aria-haspopup="dialog" data-testid="header-group-settings">
              <Settings size={20} />
            </button>
          </Tooltip>
        )}
        {(serverId || channel.type === 'GROUP_DM') && (
          <Tooltip label="Mitgliederliste">
            <button className="icon-btn" onClick={toggleMembers} aria-pressed={showMembers} data-testid="header-members">
              <Users size={20} />
            </button>
          </Tooltip>
        )}
        {extras.map((it) => {
          const Icon = EXTRA_ICONS[it.id] ?? Puzzle;
          return (
            <Tooltip key={it.id} label={it.label}>
              <button
                className="icon-btn"
                data-testid={`header-extra-${it.id}`}
                onClick={() =>
                  it.to
                    ? navigate(it.to.replace(':serverId', serverId ?? '@me').replace(':channelId', channel.id))
                    : overlays.open({ type: 'extra', item: it, ctx })
                }
              >
                <Icon size={20} />
              </button>
            </Tooltip>
          );
        })}
        <Tooltip label="Posteingang">
          <button
            className="icon-btn has-dot"
            onClick={(e) => useOverlays.getState().openPopout({ type: 'inbox', anchor: anchorFrom(e.currentTarget) })}
            data-testid="header-inbox"
            data-dot={unreadNotifications > 0 ? 'true' : undefined}
          >
            <Inbox size={20} />
          </button>
        </Tooltip>
      </div>
      {pop?.kind === 'pins' && <PinsPopover channelId={channel.id} anchor={pop.anchor} onClose={() => setPop(null)} />}
      {pop?.kind === 'threads' && <ThreadsPopover channelId={channel.id} anchor={pop.anchor} onClose={() => setPop(null)} />}
      {manageGroup && <GroupDmSettings channelId={channel.id} onClose={() => setManageGroup(false)} />}
      {topicOpen && channel.topic && (
        <Modal title={`#${channel.name}`} onClose={() => setTopicOpen(false)} testId="topic-modal">
          <MarkdownInline content={channel.topic} serverId={serverId} block />
        </Modal>
      )}
    </header>
  );
}
